import { Formik, Field, FieldProps, useFormikContext } from 'formik';
import React, { useEffect, useState } from 'react';
import { CardBody, Row, Col, Label, Form, Button } from 'reactstrap'
import { CIICategory, CalculatorYears, PORT_FUEL_FIELDS, SEA_FUEL_FIELDS, VesselTypes, apiErrorMessage, calculateCapacity, calculateReqCII, errorToast, euaMultiplyingFactor, fuelCF, isFuelSelectedElsewhere, loadFuelTypes, loadingToast, selectFuel } from './common';
import QuantityField from './QuantityField';
import Select from 'react-select';
import * as Yup from 'yup';
import { globalAPI } from './global';
import CIICalculatorModal from './CIICalculatorModal';
import { queryKeys } from './queryKeys';
import { useIsMobile, useIsStacked } from './useResponsive';

/** Port names are place names, so letters (plus the separators real names use) only. */
const PORT_NAME_REGEX = /^[A-Za-z][A-Za-z\s'.-]*$/;
const PORT_NAME_MESSAGE = 'Please enter letters only';

/** Up to 3 digits before the decimal point and 2 after. */
const SPEED_REGEX = /^\d{1,3}(\.\d{1,2})?$/;

/** Day counts: up to 3 digits, with an optional 2 decimal places. */
const DAYS_REGEX = /^\d{1,3}(\.\d{1,2})?$/;
const DAYS_MESSAGE = 'Please enter number upto 3 digits only';

/**
 * Keeps the two read-only figures — Total days and Outside ECA — in step with what
 * has been typed.
 *
 * They used to be recomputed inside each input's own onChange from `props.values`,
 * which Formik has not applied the keystroke to yet. Every derived figure therefore
 * trailed the field by one character and only caught up when the input lost focus.
 * Deriving them here instead, from committed form state, updates them as you type.
 *
 * It also closes a second hole: `steaming_days_non_eca` was only ever written by the
 * In-ECA field's handlers, so tabbing past that field submitted 0 while the screen
 * showed the full figure. Outside ECA now renders straight from form state, so what
 * is displayed and what is submitted cannot drift apart.
 */
const VoyageDerivedValues = () => {
    const { values, setFieldValue } = useFormikContext<any>();
    const distance = Number(values.distance_between) || 0;
    const speed = Number(values.speed) || 0;
    const eca = Number(values.steaming_days_eca) || 0;

    const days = distance > 0 && speed > 0
        ? ((distance / speed) / 24).toFixed(1)
        : '0.0';
    const nonEca = (Number(days) - eca).toFixed(1);

    useEffect(() => {
        if (String(values.days) !== days) {
            setFieldValue('days', days);
        }
    }, [days, values.days, setFieldValue]);

    useEffect(() => {
        if (String(values.steaming_days_non_eca) !== nonEca) {
            setFieldValue('steaming_days_non_eca', nonEca);
        }
    }, [nonEca, values.steaming_days_non_eca, setFieldValue]);

    return null;
};

const VoyageCalculator = () => {
    const [modalState, setModalState] = useState(false);
    const [recordId, setRecordId] = useState(0);
    const [fuelTypes, setFuelTypes] = useState<Array<any>>([]);
    const isMobile = useIsMobile();
    const isStacked = useIsStacked();

    /**
     * The option a fuel dropdown is holding, by the id it stores. Passing it back
     * as the select's `value` is what makes the dropdowns controlled: uncontrolled,
     * they keep showing the last choice after Reset has cleared form state.
     */
    const selectedFuel = (id: any) =>
        fuelTypes.find((option: any) => String(option.id) === String(id)) ?? null;

    useEffect(() => {
        const loadFuelTypes = async () => {
            await globalAPI
                .get(queryKeys.FuelTypesMaster.url())
                .then((res) => {
                    setFuelTypes(res.data);
                })
                .catch((err) => {
                    console.log(err);
                });
        }; loadFuelTypes();
    }, [])

    const VoyageCalculatiorFormik = {
        initailValues: {
            name_of_vessel: null,
            imo_number: null,
            type_of_vessel: null,
            dwt_of_vessel: 0,
            gross_tonnage: null,
            year: null,
            departure_port: null,
            depPortLocation: 'eu',
            arrival_port: null,
            arrPortLocation: 'eu',
            distance_between: 0,
            speed: null,
            days: 0,
            steaming_days_eca: 0,
            steaming_days_non_eca: 0,
            seaPassage: {
                fuel_type1: '',
                fuel_type1_eca_value: 0,
                fuel_type1_non_eca_value: 0,
                fuel_type2: '',
                fuel_type2_eca_value: 0,
                fuel_type2_non_eca_value: 0,
                fuel_type3: '',
                fuel_type3_eca_value: 0,
                fuel_type3_non_eca_value: 0,
            },
            totalseaPassage: {
                fuel_type1: '',
                fuel_type1_eca_value: 0,
                fuel_type1_non_eca_value: 0,
                fuel_type2: '',
                fuel_type2_eca_value: 0,
                fuel_type2_non_eca_value: 0,
                fuel_type3: '',
                fuel_type3_eca_value: 0,
                fuel_type3_non_eca_value: 0,
            },
            days_at_port: null,
            inPortLocation: 'eu',
            iceClass: 'not_applicable',
            in_port_name: '',
            portStay: {
                fuel_type1: '',
                fuel_type1_value: null,
                fuel_type2: '',
                fuel_type2_value: 0,
                fuel_type3: '',
                fuel_type3_value: 0,
            },
            totalportStay: {
                fuel_type1: '',
                fuel_type1_value: null,
                fuel_type2: '',
                fuel_type2_value: 0,
                fuel_type3: '',
                fuel_type3_value: 0,
            },
            co2_emission_at_sea: 0,
            co2_emission_in_port: 0,
            required_cii: 0,
            attained_cii: 0,
            cii_ratio: 0,
            grade: '',
            eua: 0,
            voyage_co2_emission: 0,
            name: "",
            company_name: "",
            email: "",
            mobile: null,
            i_agree: false,
            designation: ""
        },
        validationSchema: Yup.object({
            name_of_vessel: Yup.string().required('Field is required!'),
            imo_number: Yup.string().matches(/^\d{7}$/, 'Please enter 7 digits').required('This field is required!'),
            type_of_vessel: Yup.string().required('This field is required!'),
            dwt_of_vessel: Yup.string().max(6, 'Please enter number upto 6 digitis only').required('This field is required!'),
            gross_tonnage: Yup.string().max(6, 'Please enter number upto 6 digitis only').required('This field is required!'),
            year: Yup.string().required('Field is required!'),
            departure_port: Yup.string().matches(PORT_NAME_REGEX, PORT_NAME_MESSAGE).required('Field is required!'),
            arrival_port: Yup.string().matches(PORT_NAME_REGEX, PORT_NAME_MESSAGE).required('Field is required!'),
            distance_between: Yup.string().max(6, 'Please enter number upto 6 digitis only').required('This field is required!'),
            speed: Yup.string().matches(SPEED_REGEX, 'Please enter upto 3 digits and 2 decimal places').required('Field is required!'),
            steaming_days_eca: Yup.string().matches(DAYS_REGEX, DAYS_MESSAGE).required('Field is required!'),
            seaPassage: Yup.object({
                fuel_type1: Yup.number().required('Field is required!'),
                fuel_type1_eca_value: Yup.number().required('Field is required!'),
            }),
            days_at_port: Yup.string().matches(DAYS_REGEX, DAYS_MESSAGE).required('Field is required!'),
            in_port_name: Yup.string().matches(PORT_NAME_REGEX, PORT_NAME_MESSAGE).required('Field is required!'),
            portStay: Yup.object({
                fuel_type1: Yup.number().required('Field is required!'),
                fuel_type1_value: Yup.number().required('Field is required!'),
            }),
        })
    }

    const calculateSeaECACO2 = (values: any) => {
        let cf1 = fuelCF(values.seaPassage.fuel_type1) ?? 0;
        let cf2 = fuelCF(values.seaPassage.fuel_type2) ?? 0;
        let cf3 = fuelCF(values.seaPassage.fuel_type3) ?? 0;
        return ((values.seaPassage.fuel_type1_eca_value * cf1 * values.steaming_days_eca) +
            (values.seaPassage.fuel_type2_eca_value * cf2 * values.steaming_days_eca) +
            (values.seaPassage.fuel_type3_eca_value * cf3 * values.steaming_days_eca));
    }

    const calculateSeaNonECACO2 = (values: any) => {
        let cf1 = fuelCF(values.seaPassage.fuel_type1) ?? 0;
        let cf2 = fuelCF(values.seaPassage.fuel_type2) ?? 0;
        let cf3 = fuelCF(values.seaPassage.fuel_type3) ?? 0;
        return ((values.seaPassage.fuel_type1_non_eca_value * cf1 * values.steaming_days_non_eca) +
            (values.seaPassage.fuel_type2_non_eca_value * cf2 * values.steaming_days_non_eca) +
            (values.seaPassage.fuel_type3_non_eca_value * cf3 * values.steaming_days_non_eca));
    }

    const calculateSeaCO2 = (values: any) => {
        return (calculateSeaECACO2(values) + calculateSeaNonECACO2(values));
    }

    const calculatePortCO2 = (values: any) => {
        let cf1 = fuelCF(values.portStay.fuel_type1) ?? 0;
        let cf2 = fuelCF(values.portStay.fuel_type2) ?? 0;
        let cf3 = fuelCF(values.portStay.fuel_type3) ?? 0;
        return ((values.portStay.fuel_type1_value * cf1 * values.days_at_port) +
            (values.portStay.fuel_type2_value * cf2 * values.days_at_port) +
            (values.portStay.fuel_type3_value * cf3 * values.days_at_port));
    }

    const calculateSeaEUA = (values: any) => {
        let SEAEUA = 0;
        if (values.depPortLocation === 'eu' && values.arrPortLocation === 'eu') {
            SEAEUA = calculateSeaCO2(values) * euaMultiplyingFactor(values.year) * 1
            return (SEAEUA);
        } else if ((values.depPortLocation === 'eu' && values.arrPortLocation !== 'eu') ||
            (values.depPortLocation !== 'eu' && values.arrPortLocation === 'eu')) {
            return calculateSeaCO2(values) * euaMultiplyingFactor(values.year) * 0.5;
        }
        else {
            return 0;
        }
    }

    const calculatePortEUA = (values: any) => {
        let PortEUA = 0;
        if (values.inPortLocation === 'eu') {
            PortEUA = calculatePortCO2(values) * euaMultiplyingFactor(values.year) * 1;
            return PortEUA;
        } else {
            return 0;
        }
    }

    const calculateTotalEUA = (values: any) => {
        if (values.iceClass === 'ia') {
            return ((calculateSeaEUA(values) + calculatePortEUA(values)) * 0.95).toFixed(0);
        } else {
            return (calculateSeaEUA(values) + calculatePortEUA(values)).toFixed(0);
        }
    }

    return (
        <CardBody>
            <h5 className='mt-1 mb-4'>Voyage Calculator</h5>
            <Formik
                onSubmit={(values: any, actions: any) => {
                    actions.setSubmitting(false);
                    loadingToast('Please wait! we are calculating...')
                    values.co2_emission_at_sea = calculateSeaCO2(values).toFixed(4);
                    values.co2_emission_in_port = calculatePortCO2(values).toFixed(4);
                    values.required_cii = calculateReqCII(values).toFixed(4);
                    values.attained_cii = (((calculateSeaCO2(values) + calculatePortCO2(values)) /
                        (calculateCapacity(values) * values.distance_between)) * 1000000).toFixed(4);
                    values.cii_ratio = (((((calculateSeaCO2(values) + calculatePortCO2(values)) /
                        (calculateCapacity(values) * values.distance_between)) * 1000000) / calculateReqCII(values))).toFixed(4);
                    values.voyage_co2_emission = (calculateSeaCO2(values) + calculatePortCO2(values)).toFixed(4);
                    values.grade = CIICategory(values, (((((calculateSeaCO2(values) + calculatePortCO2(values)) /
                        (calculateCapacity(values) * values.distance_between)) * 1000000) / calculateReqCII(values))));
                    values.eua = calculateTotalEUA(values)
                    globalAPI.post(`voyage_calculator/`, values).then(res => {
                        if (res.status === 201) {
                            setRecordId(res.data.id);
                            setModalState(true);
                        }
                    }).catch(err => {
                        errorToast('Error! ' + apiErrorMessage(err));
                    });
                }}
                initialValues={VoyageCalculatiorFormik.initailValues}
                validationSchema={VoyageCalculatiorFormik.validationSchema}
            >
                {props => (
                    <Form onSubmit={props.handleSubmit} noValidate autoComplete='off'>
                        <VoyageDerivedValues />
                        <Row className={isMobile ? '' : 'mb-3'}>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Name of vessel</Label>
                                <Field
                                    type="text"
                                    id='name_of_vessel'
                                    name='name_of_vessel'
                                    className='form-control mb-2'
                                />
                                {props.touched.name_of_vessel && props.errors.name_of_vessel &&
                                    <Label className='error'>{props.errors.name_of_vessel}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>IMO number</Label>
                                <QuantityField
                                    name='imo_number'
                                    id='imo_number'
                                    className='form-control mb-2'
                                    maxWholeDigits={7}
                                    maxDecimals={0}
                                    showNotice
                                />
                                {props.touched.imo_number && props.errors.imo_number &&
                                    <Label className='error'>{props.errors.imo_number}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Type of vessel</Label>
                                <Field name='type_of_vessel'>
                                    {({ field, form, }: FieldProps) => (
                                        <Select
                                            options={VesselTypes}
                                            value={VesselTypes.find((option: any) => option.label === field.value) ?? null}
                                            getOptionLabel={(option: any) => option.label}
                                            getOptionValue={(option: any) => option.label}
                                            onChange={(e: any) => {
                                                form.setFieldValue(field.name, e.label);
                                            }}
                                            onBlur={() => {
                                                form.setFieldTouched(field.name, true);
                                            }}
                                            className='mb-2'
                                        />
                                    )}
                                </Field>
                                {props.touched.type_of_vessel && props.errors.type_of_vessel &&
                                    <Label className='error'>{props.errors.type_of_vessel}</Label>
                                }
                            </Col>
                        </Row>
                        <Row className={isMobile ? '' : 'mb-3'}>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>DWT of vessel</Label>
                                <QuantityField
                                    name='dwt_of_vessel'
                                    id='dwt_of_vessel'
                                    unit='mt'
                                    maxDecimals={0}
                                />
                                {props.touched.dwt_of_vessel && props.errors.dwt_of_vessel &&
                                    <Label className='error'>{props.errors.dwt_of_vessel}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Gross tonnage</Label>
                                <QuantityField
                                    name='gross_tonnage'
                                    id='gross_tonnage'
                                    unit='mt'
                                    maxDecimals={0}
                                />
                                {props.touched.gross_tonnage && props.errors.gross_tonnage &&
                                    <Label className='error'>{props.errors.gross_tonnage}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Year</Label>
                                <Field name='year'>
                                    {({ field, form, }: FieldProps) => (
                                        <Select
                                            options={CalculatorYears}
                                            value={CalculatorYears.find((option: any) => option.label === field.value) ?? null}
                                            getOptionLabel={(option: any) => option.label}
                                            getOptionValue={(option: any) => option.label}
                                            onChange={(e: any) => {
                                                form.setFieldValue(field.name, e.label);
                                            }}
                                            onBlur={() => {
                                                form.setFieldTouched(field.name, true);
                                            }}
                                            className='mb-2'
                                        />
                                    )}
                                </Field>
                                {props.touched.year && props.errors.year &&
                                    <Label className='error'>{props.errors.year}</Label>
                                }
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0'>Ice class applicability</Label>
                                <div className="radio-group">
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="iceClass"
                                            id="iceClass_ia"
                                            value="ia"
                                        />
                                        <Label className="form-check-label" htmlFor="iceClass_ia">
                                            IA/IA Super
                                        </Label>
                                    </div>
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="iceClass"
                                            id="iceClass_na"
                                            value="not_applicable"
                                        />
                                        <Label className="form-check-label" htmlFor="iceClass_na">
                                            NA
                                        </Label>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <h5>At Sea Operation</h5>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Departure port</Label>
                                <Field
                                    type="text"
                                    id='departure_port'
                                    name='departure_port'
                                    className='form-control mb-2'
                                />
                                {props.touched.departure_port && props.errors.departure_port &&
                                    <Label className='error'>{props.errors.departure_port}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className="radio-group field-aligned">
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="depPortLocation"
                                            id="depPortLocation_eu"
                                            value="eu"
                                        />
                                        <Label className="form-check-label" htmlFor="depPortLocation_eu">
                                            EU
                                        </Label>
                                    </div>
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="depPortLocation"
                                            id="depPortLocation_non_eu"
                                            value="non_eu"
                                        />
                                        <Label className="form-check-label" htmlFor="depPortLocation_non_eu">
                                            NON EU
                                        </Label>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Arrival port</Label>
                                <Field
                                    type="text"
                                    id='arrival_port'
                                    name='arrival_port'
                                    className='form-control mb-2'
                                />
                                {props.touched.arrival_port && props.errors.arrival_port &&
                                    <Label className='error'>{props.errors.arrival_port}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className="radio-group field-aligned">
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="arrPortLocation"
                                            id="arrPortLocation_eu"
                                            value="eu"
                                        />
                                        <Label className="form-check-label" htmlFor="arrPortLocation_eu">
                                            EU
                                        </Label>
                                    </div>
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="arrPortLocation"
                                            id="arrPortLocation_non_eu"
                                            value="non_eu"
                                        />
                                        <Label className="form-check-label" htmlFor="arrPortLocation_non_eu">
                                            NON EU
                                        </Label>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Distance between the ports</Label>
                                <QuantityField
                                    name='distance_between'
                                    id='distance_between'
                                    unit='nm'
                                    maxDecimals={0}
                                />
                                {props.touched.distance_between && props.errors.distance_between &&
                                    <Label className='error'>{props.errors.distance_between}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Speed</Label>
                                <QuantityField
                                    name='speed'
                                    id='speed'
                                    unit='knots'
                                    maxWholeDigits={3}
                                    maxDecimals={2}
                                />
                                {props.touched.speed && props.errors.speed &&
                                    <Label className='error'>{props.errors.speed}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Total days</Label>
                                <Field
                                    type="text"
                                    id='days'
                                    name='days'
                                    className='form-control mb-2'
                                    disabled
                                />
                                {props.touched.days && props.errors.days &&
                                    <Label className='error'>{props.errors.days}</Label>
                                }
                            </Col>
                        </Row>
                        <Row>
                            <h6>Steaming days</h6>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>In ECA</Label>
                                <QuantityField
                                    name='steaming_days_eca'
                                    id='steaming_days_eca'
                                    className='form-control mb-2'
                                    maxWholeDigits={3}
                                    maxDecimals={2}
                                    showNotice
                                />
                                {props.touched.steaming_days_eca && props.errors.steaming_days_eca &&
                                    <Label className='error'>{props.errors.steaming_days_eca}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0'>Outside ECA</Label>
                                {/* No `value` override: rendering straight from form
                                    state is what guarantees the figure shown is the
                                    figure submitted. */}
                                <Field
                                    type="text"
                                    id='steaming_days_non_eca'
                                    name='steaming_days_non_eca'
                                    className='form-control mb-2'
                                    disabled
                                />
                            </Col>
                        </Row>
                        <Row>
                            <h6>Fuel consumption per day during sea passage</h6>
                        </Row>
                        {!isStacked &&
                            <Row>
                                <Col lg={4}>
                                    <Label>Type</Label>
                                </Col>
                                <Col lg={4}>
                                    <Label>Quantity — in ECA</Label>
                                </Col>
                                <Col lg={4}>
                                    <Label>Quantity — outside ECA</Label>
                                </Col>
                            </Row>
                        }
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Field
                                    name="seaPassage.fuel_type1">
                                    {({ field, form }: FieldProps) => (
                                        <Select
                                            name={field.name}
                                            options={fuelTypes}
                                            value={selectedFuel(field.value)}
                                            getOptionLabel={(option: any) => option?.fuel_type_name}
                                            getOptionValue={(option: any) => option?.id}
                                            isOptionDisabled={(option: any) =>
                                                isFuelSelectedElsewhere(form.values, field.name,
                                                    field.name.startsWith('portStay') ? PORT_FUEL_FIELDS : SEA_FUEL_FIELDS,
                                                    option?.id)}
                                            onChange={(e: any) => {
                                                selectFuel(form, field.name, 'totalseaPassage.fuel_type1', e);
                                            }}
                                            onBlur={() => {
                                                form.setFieldTouched(field.name, true);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            className='mb-2'
                                            placeholder={isStacked ? "Type of fuel" : null}
                                        />
                                    )}
                                </Field>
                                {props.touched?.seaPassage?.fuel_type1 && props.errors?.seaPassage?.fuel_type1 &&
                                    <Label className='error'>{props.errors.seaPassage.fuel_type1}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='seaPassage.fuel_type1_eca_value'
                                        id='seaPassage.fuel_type1_eca_value'
                                        placeholder={isStacked ? "Consumption in ECA" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalseaPassage.fuel_type1_eca_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                                {props.touched?.seaPassage?.fuel_type1_eca_value && props.errors?.seaPassage?.fuel_type1_eca_value &&
                                    <Label className='error'>{props.errors.seaPassage.fuel_type1_eca_value}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='seaPassage.fuel_type1_non_eca_value'
                                        id='seaPassage.fuel_type1_non_eca_value'
                                        placeholder={isStacked ? "Consumption outside ECA" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalseaPassage.fuel_type1_non_eca_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Field
                                    name="seaPassage.fuel_type2">
                                    {({ field, form }: FieldProps) => (
                                        <Select
                                            name={field.name}
                                            options={fuelTypes}
                                            value={selectedFuel(field.value)}
                                            getOptionLabel={(option: any) => option?.fuel_type_name}
                                            getOptionValue={(option: any) => option?.id}
                                            isOptionDisabled={(option: any) =>
                                                isFuelSelectedElsewhere(form.values, field.name,
                                                    field.name.startsWith('portStay') ? PORT_FUEL_FIELDS : SEA_FUEL_FIELDS,
                                                    option?.id)}
                                            onChange={(e: any) => {
                                                selectFuel(form, field.name, 'totalseaPassage.fuel_type2', e);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            className='mb-2'
                                            placeholder={isStacked ? "Type of fuel" : null}
                                        />
                                    )}
                                </Field>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='seaPassage.fuel_type2_eca_value'
                                        id='seaPassage.fuel_type2_eca_value'
                                        placeholder={isStacked ? "Consumption in ECA" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalseaPassage.fuel_type2_eca_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='seaPassage.fuel_type2_non_eca_value'
                                        id='seaPassage.fuel_type2_non_eca_value'
                                        placeholder={isStacked ? "Consumption outside ECA" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalseaPassage.fuel_type2_non_eca_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Field
                                    name="seaPassage.fuel_type3">
                                    {({ field, form }: FieldProps) => (
                                        <Select
                                            name={field.name}
                                            options={fuelTypes}
                                            value={selectedFuel(field.value)}
                                            getOptionLabel={(option: any) => option?.fuel_type_name}
                                            getOptionValue={(option: any) => option?.id}
                                            isOptionDisabled={(option: any) =>
                                                isFuelSelectedElsewhere(form.values, field.name,
                                                    field.name.startsWith('portStay') ? PORT_FUEL_FIELDS : SEA_FUEL_FIELDS,
                                                    option?.id)}
                                            onChange={(e: any) => {
                                                selectFuel(form, field.name, 'totalseaPassage.fuel_type3', e);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            className='mb-2'
                                            placeholder={isStacked ? "Type of fuel" : null}
                                        />
                                    )}
                                </Field>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='seaPassage.fuel_type3_eca_value'
                                        id='seaPassage.fuel_type3_eca_value'
                                        placeholder={isStacked ? "Consumption in ECA" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalseaPassage.fuel_type3_eca_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='seaPassage.fuel_type3_non_eca_value'
                                        id='seaPassage.fuel_type3_non_eca_value'
                                        placeholder={isStacked ? "Consumption outside ECA" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalseaPassage.fuel_type3_non_eca_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <h5>In Port operation</h5>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Name of port</Label>
                                <Field
                                    type="text"
                                    id='in_port_name'
                                    name='in_port_name'
                                    className='form-control mb-2'
                                />
                                {props.touched.in_port_name && props.errors.in_port_name &&
                                    <Label className='error'>{props.errors.in_port_name}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className="radio-group field-aligned">
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="inPortLocation"
                                            id="inPortLocation_eu"
                                            value="eu"
                                        />
                                        <Label className="form-check-label" htmlFor="inPortLocation_eu">
                                            EU
                                        </Label>
                                    </div>
                                    <div className="form-check">
                                        <Field
                                            className="form-check-input"
                                            type="radio"
                                            name="inPortLocation"
                                            id="inPortLocation_non_eu"
                                            value="non_eu"
                                        />
                                        <Label className="form-check-label" htmlFor="inPortLocation_non_eu">
                                            NON EU
                                        </Label>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Label className='mb-0 asteric'>Estimated days at port</Label>
                                <QuantityField
                                    name='days_at_port'
                                    id='days_at_port'
                                    className='form-control mb-2'
                                    maxWholeDigits={3}
                                    maxDecimals={2}
                                    showNotice
                                />
                                {props.touched.days_at_port && props.errors.days_at_port &&
                                    <Label className='error'>{props.errors.days_at_port}</Label>
                                }
                            </Col>
                        </Row>
                        <Row>
                            <h6>Fuel consumption per day during port stay</h6>
                        </Row>
                        {!isStacked &&
                            <Row>
                                <Col lg={4}>
                                    <Label>Type</Label>
                                </Col>
                                <Col lg={4}>
                                    <Label>Quantity</Label>
                                </Col>
                            </Row>
                        }
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Field
                                    name="portStay.fuel_type1">
                                    {({ field, form }: FieldProps) => (
                                        <Select
                                            name={field.name}
                                            options={fuelTypes}
                                            value={selectedFuel(field.value)}
                                            getOptionLabel={(option: any) => option?.fuel_type_name}
                                            getOptionValue={(option: any) => option?.id}
                                            isOptionDisabled={(option: any) =>
                                                isFuelSelectedElsewhere(form.values, field.name,
                                                    field.name.startsWith('portStay') ? PORT_FUEL_FIELDS : SEA_FUEL_FIELDS,
                                                    option?.id)}
                                            onChange={(e: any) => {
                                                selectFuel(form, field.name, 'totalportStay.fuel_type1', e);
                                            }}
                                            onBlur={() => {
                                                form.setFieldTouched(field.name, true);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            className='mb-2'
                                            placeholder={isStacked ? "Type of fuel" : null}
                                        />
                                    )}
                                </Field>
                                {props.touched?.portStay?.fuel_type1 && props.errors?.portStay?.fuel_type1 &&
                                    <Label className='error'>{props.errors.portStay.fuel_type1}</Label>
                                }
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='portStay.fuel_type1_value'
                                        id='portStay.fuel_type1_value'
                                        placeholder={isStacked ? "Consumption" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalportStay.fuel_type1_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                                {props.touched?.portStay?.fuel_type1_value && props.errors?.portStay?.fuel_type1_value &&
                                    <Label className='error'>{props.errors.portStay.fuel_type1_value}</Label>
                                }
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Field
                                    name="portStay.fuel_type2">
                                    {({ field, form }: FieldProps) => (
                                        <Select
                                            name={field.name}
                                            options={fuelTypes}
                                            value={selectedFuel(field.value)}
                                            getOptionLabel={(option: any) => option?.fuel_type_name}
                                            getOptionValue={(option: any) => option?.id}
                                            isOptionDisabled={(option: any) =>
                                                isFuelSelectedElsewhere(form.values, field.name,
                                                    field.name.startsWith('portStay') ? PORT_FUEL_FIELDS : SEA_FUEL_FIELDS,
                                                    option?.id)}
                                            onChange={(e: any) => {
                                                selectFuel(form, field.name, 'totalportStay.fuel_type2', e);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            className='mb-2'
                                            placeholder={isStacked ? "Type of fuel" : null}
                                        />
                                    )}
                                </Field>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='portStay.fuel_type2_value'
                                        id='portStay.fuel_type2_value'
                                        placeholder={isStacked ? "Consumption" : ""}
                                        onBlur={(e: any) => {
                                                props.handleChange(e);
                                                props.setFieldValue('totalportStay.fuel_type2_value',
                                                    e.target.value * props?.values?.days)
                                            }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                        </Row>
                        <Row className='mb-3'>
                            <Col xs={12} md={6} lg={4}>
                                <Field
                                    name="portStay.fuel_type3">
                                    {({ field, form }: FieldProps) => (
                                        <Select
                                            name={field.name}
                                            options={fuelTypes}
                                            value={selectedFuel(field.value)}
                                            getOptionLabel={(option: any) => option?.fuel_type_name}
                                            getOptionValue={(option: any) => option?.id}
                                            isOptionDisabled={(option: any) =>
                                                isFuelSelectedElsewhere(form.values, field.name,
                                                    field.name.startsWith('portStay') ? PORT_FUEL_FIELDS : SEA_FUEL_FIELDS,
                                                    option?.id)}
                                            onChange={(e: any) => {
                                                selectFuel(form, field.name, 'totalportStay.fuel_type3', e);
                                            }}
                                            isClearable={true}
                                            menuPortalTarget={document.body}
                                            className='mb-2'
                                            placeholder={isStacked ? "Type of fuel" : null}
                                        />
                                    )}

                                </Field>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <div className='input-group mb-2'>
                                    <QuantityField
                                        name='portStay.fuel_type3_value'
                                        id='portStay.fuel_type3_value'
                                        placeholder={isStacked ? "Consumption" : ""}
                                        onBlur={(e: any) => {
                                            props.handleChange(e);
                                            props.setFieldValue('totalportStay.fuel_type3_value',
                                                e.target.value * props?.values?.days)
                                        }}
                                    />
                                    <div className='input-group-text'>mt</div>
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <div className='form-actions mt-5'>
                                    <Button className='btn btn-primary' type='submit'>Calculate</Button>
                                    <Button className='btn btn-primary' type='reset' onClick={() => props.resetForm({
                                        values: VoyageCalculatiorFormik.initailValues
                                    })}>Reset</Button>
                                </div>
                            </Col>
                        </Row>
                        {modalState === true &&
                            <CIICalculatorModal
                                state={modalState}
                                setState={setModalState}
                                id={recordId}
                                calculator='voyage'
                                calculateSeaCO2={calculateSeaCO2(props.values)}
                                calculatePortCO2={calculatePortCO2(props.values)}
                                calculateTotalEUA={calculateTotalEUA(props.values)}
                            />
                        }
                    </Form>
                )}
            </Formik>
        </CardBody >
    )
}

export default VoyageCalculator